import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { adminApi } from "@/admin/api";
import { api as consumerApi } from "@/lib/api";
import { toast } from "sonner";

export default function AdminLineups() {
  const qc = useQueryClient();
  const [gwFilter, setGwFilter] = useState("");
  const { data: rows } = useQuery({
    queryKey: ["admin-lineups", gwFilter],
    queryFn: async () =>
      (await adminApi.get("/predicted-lineups", { params: gwFilter ? { gameweek: Number(gwFilter) } : {} })).data,
    staleTime: 30_000,
  });
  const { data: boot } = useQuery({
    queryKey: ["boot-for-admin"],
    queryFn: async () => (await consumerApi.get("/bootstrap")).data,
    staleTime: 5 * 60_000,
  });
  const [form, setForm] = useState({ team_id: "", gameweek: "", player_id: "", start_probability: 80, source_note: "" });
  const teams = boot?.teams || [];
  const teamById = (id) => teams.find((t) => t.id === id)?.short_name || `T${id}`;

  const create = useMutation({
    mutationFn: async (body) => (await adminApi.post("/predicted-lineups", body)).data,
    onSuccess: () => {
      toast.success("Lineup entry added");
      setForm({ team_id: "", gameweek: "", player_id: "", start_probability: 80, source_note: "" });
      qc.invalidateQueries({ queryKey: ["admin-lineups"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Create failed"),
  });
  const del = useMutation({
    mutationFn: async (id) => (await adminApi.delete(`/predicted-lineups/${id}`)).data,
    onSuccess: () => {
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["admin-lineups"] });
    },
  });

  const submit = (e) => {
    e.preventDefault();
    if (!form.team_id || !form.gameweek || !form.player_id) {
      toast.error("Team, GW and player ID required");
      return;
    }
    create.mutate({
      team_id: Number(form.team_id),
      gameweek: Number(form.gameweek),
      player_id: Number(form.player_id),
      start_probability: Number(form.start_probability),
      source_note: form.source_note || null,
    });
  };

  return (
    <div className="space-y-4 animate-fade-up">
      <div>
        <div className="mono uppercase text-[11px] tracking-widest text-totem-textDim">Editorial</div>
        <h1 className="display-heading text-3xl">Predicted lineups</h1>
      </div>

      <div className="flex items-center gap-2">
        <label className="label-caps">Filter GW</label>
        <input
          type="number"
          min={1}
          max={38}
          value={gwFilter}
          onChange={(e) => setGwFilter(e.target.value)}
          className="bg-totem-surface border border-totem-border rounded px-2 py-1 text-sm mono w-24"
          placeholder="All"
        />
      </div>

      <form onSubmit={submit} className="totem-card p-4 grid grid-cols-1 sm:grid-cols-6 gap-2">
        <select
          value={form.team_id}
          onChange={(e) => setForm({ ...form, team_id: e.target.value })}
          className="bg-totem-surface border border-totem-border rounded px-2 py-2 text-sm"
        >
          <option value="">Team…</option>
          {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <input
          type="number"
          placeholder="GW"
          min={1}
          max={38}
          value={form.gameweek}
          onChange={(e) => setForm({ ...form, gameweek: e.target.value })}
          className="bg-totem-surface border border-totem-border rounded px-2 py-2 text-sm mono"
        />
        <input
          type="number"
          placeholder="Player ID"
          value={form.player_id}
          onChange={(e) => setForm({ ...form, player_id: e.target.value })}
          className="bg-totem-surface border border-totem-border rounded px-2 py-2 text-sm mono"
        />
        <input
          type="number"
          placeholder="Start %"
          min={0}
          max={100}
          value={form.start_probability}
          onChange={(e) => setForm({ ...form, start_probability: e.target.value })}
          className="bg-totem-surface border border-totem-border rounded px-2 py-2 text-sm mono"
        />
        <input
          placeholder="Source note"
          value={form.source_note}
          onChange={(e) => setForm({ ...form, source_note: e.target.value })}
          className="bg-totem-surface border border-totem-border rounded px-2 py-2 text-sm sm:col-span-1"
        />
        <button
          type="submit"
          disabled={create.isPending}
          className="bg-totem-green text-totem-bg font-semibold rounded px-3 py-2 text-sm flex items-center justify-center gap-1"
        >
          <Plus size={14} /> Add
        </button>
      </form>

      <div className="totem-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-totem-surface">
            <tr>
              <th className="text-left px-3 py-2 label-caps">Team</th>
              <th className="text-left px-3 py-2 label-caps">GW</th>
              <th className="text-left px-3 py-2 label-caps">Player ID</th>
              <th className="text-left px-3 py-2 label-caps">Start %</th>
              <th className="text-left px-3 py-2 label-caps">Note</th>
              <th className="text-right px-3 py-2 label-caps">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(rows?.results || []).length === 0 && (
              <tr><td colSpan={6} className="p-4 text-center text-totem-textDim">No predicted lineups yet.</td></tr>
            )}
            {(rows?.results || []).map((r) => (
              <tr key={r.id} className="border-t border-totem-borderSoft">
                <td className="px-3 py-2">{teamById(r.team_id)}</td>
                <td className="px-3 py-2 mono">GW{r.gameweek}</td>
                <td className="px-3 py-2 mono">{r.player_id}</td>
                <td className="px-3 py-2 mono">{r.start_probability}%</td>
                <td className="px-3 py-2 text-totem-textDim text-xs truncate max-w-[240px]">{r.source_note || "—"}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => del.mutate(r.id)}
                    className="text-totem-red hover:brightness-110 inline-flex items-center gap-1 text-xs mono"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
